import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

// v4의 apps/gate/src/token.html 배포 경로 규칙(libs/api/.../auth.service.ts#createIframe과
// 동일) — https 배포는 gate/ 하위 경로, http(local dev)는 루트.
const TOKEN_HTML_PATH_HTTPS = '/gate/token.html';
const TOKEN_HTML_PATH_OTHER = '/token.html';
const BRIDGE_TIMEOUT_MS = 5000;

type BridgeStatus = 'ok' | 'no-session' | 'failed';

function isBridgeResultMessage(
  data: unknown,
  resultMessage: string,
): data is { message: string; status: BridgeStatus } {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { message?: unknown }).message === resultMessage
  );
}

// 신규(schoolbell-e-web) ↔ 레거시(sbe-web-v4) 세션 브리지. v4의 token.html(sbe-web-v4 레포,
// docs/engineering/adopt-legacy-session-handoff.md 참고)을 hidden iframe으로 띄워 세 방향의
// 메시지를 주고받는다 — 어느 방향이든 durable 토큰(레거시 refresh token이든 신규 access
// token이든)은 이 클래스나 postMessage 페이로드에 절대 담기지 않는다(상태 문자열만 오간다).
@Injectable({ providedIn: 'root' })
export class LegacyHandoffService {
  private adopted = false;

  // 앱 부팅 시 1회 — 이미 v4에 로그인돼 있으면 재로그인 없이 신규 세션으로 계승한다.
  // status==='ok'면 백엔드 adoptLegacy()(user-auth.controller.ts)가 sbe_spinoff_refresh
  // 쿠키를 함께 발급한다 — 이 함수 자신은 상태 문자열만 다루고 세션 복원은 하지 않는다.
  // 반환하는 boolean은 그 성공 여부(status==='ok')다 — 호출부(app.config.ts)가 true일 때만
  // SpinoffAuthService.init()을 한 번 더 불러 방금 받은 쿠키로 실제 세션을 복원한다(2026-08-17
  // spinoff-auth-pattern-alignment 플랜 후속 — 이전엔 이 2단계가 구 라우트에 의존해 조용히
  // no-op이었다. 코드 리뷰 지적 — 성공 여부를 반환하지 않으면 미인증 사용자도 매번
  // auth.init()이 불필요하게 재호출돼 중복 네트워크 요청이 생긴다).
  async tryAdopt(): Promise<boolean> {
    if (this.adopted) return false;
    this.adopted = true;

    const status = await this.bridge('adoptLegacySession', 'adoptLegacySessionResult');
    return status === 'ok';
  }

  // schoolbell-e-web에 새로 로그인했을 때(계승이 아니라 phone+password 로그인) — v4에도
  // 세션을 채워 넣어 사용자가 v4를 직접 방문해도 로그인 상태로 보이게 한다. 실패해도 신규
  // 앱 로그인 자체에는 영향 없는 best-effort — 호출부는 await 없이 fire-and-forget으로 쓴다.
  async notifyProvision(): Promise<void> {
    await this.bridge('provisionLegacySession', 'provisionLegacySessionResult');
  }

  // schoolbell-e-web 로그아웃 시 이 브라우저의 v4 로그인 흔적을 지운다(v4 자신의 로그아웃과
  // 동일하게 클라이언트 측 clear뿐 — 서버 측 레거시 refresh token은 revoke하지 않는다. 그
  // 값은 유저당 1개뿐이라 revoke하면 그 유저의 v4 모든 기기가 로그아웃되므로 의도적으로
  // 하지 않음). best-effort — 호출부는 await 없이 fire-and-forget으로 쓴다.
  async notifyLogout(): Promise<void> {
    await this.bridge('clearLegacySession', 'clearLegacySessionResult');
  }

  private bridge(triggerMessage: string, resultMessage: string): Promise<BridgeStatus> {
    // 후행 슬래시가 있으면(env 설정 실수) event.origin과의 정확 일치 비교가 항상 실패해
    // 타임아웃까지만 대기하게 된다 — 정규화한다(Gemini 리뷰 반영).
    const legacyAuthOrigin = environment.legacyAuthOrigin?.replace(/\/$/, '');
    if (!legacyAuthOrigin) return Promise.resolve('no-session');

    return new Promise((resolve) => {
      let settled = false;
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
      const tokenHtmlPath = legacyAuthOrigin.startsWith('https://')
        ? TOKEN_HTML_PATH_HTTPS
        : TOKEN_HTML_PATH_OTHER;
      iframe.src = `${legacyAuthOrigin}${tokenHtmlPath}`;

      const settle = (status: BridgeStatus) => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMessage);
        clearTimeout(timer);
        iframe.remove();
        resolve(status);
      };

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== legacyAuthOrigin) return;
        if (event.source !== iframe.contentWindow) return;
        if (!isBridgeResultMessage(event.data, resultMessage)) return;
        settle(event.data.status);
      };

      const timer = setTimeout(() => settle('failed'), BRIDGE_TIMEOUT_MS);

      window.addEventListener('message', onMessage);
      iframe.onload = () => {
        iframe.contentWindow?.postMessage({ message: triggerMessage }, legacyAuthOrigin);
      };
      document.body.appendChild(iframe);
    });
  }
}
